import React from 'react';
import {
  Calendar,
  Briefcase,
  Eye,
  FileText,
  Clock,
  Zap,
  TrendingUp,
  AlertCircle
} from 'lucide-react';
import { PracticeAnalyticsSummary } from '../../types/practiceManagement';
import { Language } from '../../types/kundali';

interface PracticeMetricCardsProps {
  analytics: PracticeAnalyticsSummary;
  todayConsultationsCount: number;
  upcomingTimingCount: number;
  lang: Language;
  onSelectMetric?: (metricKey: string) => void;
}

export const PracticeMetricCards: React.FC<PracticeMetricCardsProps> = ({
  analytics,
  todayConsultationsCount,
  upcomingTimingCount,
  lang,
  onSelectMetric
}) => {
  const isHi = lang === 'hi';

  const metrics = [
    {
      id: 'consultations_today',
      titleEn: "Today's Consultations",
      titleHi: 'आज के परामर्श सत्र',
      value: todayConsultationsCount,
      subtextEn: `${analytics.consultationsConducted} completed this month`,
      subtextHi: `इस माह ${analytics.consultationsConducted} संपन्न`,
      icon: Calendar,
      accent: 'border-astro-accent/30 text-amber-600 dark:text-amber-400 bg-astro-secondary-surface0/10',
      badge: todayConsultationsCount > 0 ? (isHi ? 'सक्रिय' : 'Live') : undefined,
      badgeColor: 'bg-emerald-500/20 text-emerald-800 dark:text-emerald-300'
    },
    {
      id: 'active_cases',
      titleEn: 'Active Cases',
      titleHi: 'सक्रिय प्रकरण (केस)',
      value: analytics.totalActiveCases,
      subtextEn: `${analytics.casesCompletedInPeriod} delivered successfully`,
      subtextHi: `${analytics.casesCompletedInPeriod} पूर्ण वितरित`,
      icon: Briefcase,
      accent: 'border-blue-500/30 text-blue-600 dark:text-blue-400 bg-blue-500/10',
      badge: `${analytics.totalActiveCases} ${isHi ? 'प्रकरण' : 'in flow'}`
    },
    {
      id: 'awaiting_review',
      titleEn: 'Awaiting Review',
      titleHi: 'समीक्षा हेतु लंबित',
      value: analytics.reportsAwaitingReview,
      subtextEn: 'Dossiers pending quality approval',
      subtextHi: 'प्रमाण अनुमोदन प्रतीक्षारत',
      icon: Eye,
      accent: 'border-orange-500/30 text-orange-600 dark:text-orange-400 bg-orange-500/10',
      badge: analytics.reportsAwaitingReview > 0 ? (isHi ? 'ध्यानाकर्षण' : 'Action') : undefined,
      badgeColor: 'bg-astro-secondary-surface0/20 text-astro-secondary dark:text-amber-300'
    },
    {
      id: 'reports_pending',
      titleEn: 'Reports Pending',
      titleHi: 'लंबित रिपोर्ट ड्राफ्ट',
      value: analytics.pipelineDistribution.REPORT_PREP || 0,
      subtextEn: `${analytics.averageReportTurnaroundDays}d avg turnaround`,
      subtextHi: `औसत ${analytics.averageReportTurnaroundDays} दिन चक्र`,
      icon: FileText,
      accent: 'border-purple-500/30 text-purple-600 dark:text-purple-400 bg-purple-500/10'
    },
    {
      id: 'follow_ups_due',
      titleEn: 'Follow-Ups Due',
      titleHi: 'लंबित फॉलो-अप',
      value: analytics.followUpsDueCount,
      subtextEn: `${analytics.followUpsCompletedRate}% completion rate`,
      subtextHi: `${analytics.followUpsCompletedRate}% समापन दर`,
      icon: Clock,
      accent: 'border-emerald-500/30 text-emerald-600 dark:text-emerald-400 bg-emerald-500/10',
      badge: analytics.followUpsDueCount > 0 ? (isHi ? 'समयबद्ध' : 'Due') : undefined,
      badgeColor: 'bg-emerald-500/20 text-emerald-800 dark:text-emerald-300'
    },
    {
      id: 'timing_events',
      titleEn: 'Timing Events (30d)',
      titleHi: 'आगामी गोचर व दशा काल',
      value: upcomingTimingCount,
      subtextEn: 'High confluence planetary windows',
      subtextHi: 'सटीक ग्रह संमिलन काल',
      icon: Zap,
      accent: 'border-amber-600/40 text-[#52635F] dark:text-amber-300 bg-astro-secondary/15',
      badge: isHi ? 'रडार सक्रिय' : 'Radar Active',
      badgeColor: 'bg-astro-secondary-surface0/20 text-astro-secondary dark:text-amber-300'
    }
  ];

  return (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3.5">
      {metrics.map((m) => {
        const Icon = m.icon;
        return (
          <div
            key={m.id}
            onClick={() => onSelectMetric && onSelectMetric(m.id)}
            className="group relative bg-white dark:bg-slate-850 p-3.5 rounded-xl border border-astro-border/80 dark:border-astro-strong-border/70 shadow-xs hover:shadow-md transition-all duration-200 hover:border-amber-400/50 cursor-pointer select-none"
          >
            <div className="flex items-center justify-between gap-2 mb-2">
              <div className={`p-2 rounded-lg ${m.accent}`}>
                <Icon className="w-4 h-4" />
              </div>
              {m.badge && (
                <span
                  className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${
                    m.badgeColor || 'bg-astro-secondary-surface dark:bg-astro-secondary text-astro-secondary-text dark:text-astro-muted'
                  }`}
                >
                  {m.badge}
                </span>
              )}
            </div>

            <div className="space-y-0.5">
              <div className="text-2xl font-black text-astro-text dark:text-astro-muted tracking-tight">
                {m.value}
              </div>
              <div className="text-xs font-semibold text-astro-secondary-text dark:text-astro-muted truncate">
                {isHi ? m.titleHi : m.titleEn}
              </div>
              <div className="text-[11px] text-astro-muted dark:text-astro-muted truncate pt-0.5">
                {isHi ? m.subtextHi : m.subtextEn}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};
